import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../lib/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'owner' | 'master' | 'admin' | 'user';
  requiredPermission?: string;
}

export function ProtectedRoute({ children, requiredRole, requiredPermission }: ProtectedRouteProps) {
  const { profile, loading, hasPermission, hasRole, user } = useAuth();

  // Debug logs
  React.useEffect(() => {
    console.log('🛡️ ProtectedRoute: Estado actual', {
      loading,
      hasUser: !!user,
      hasProfile: !!profile,
      userEmail: user?.email,
      profileRole: profile?.role,
    });
  }, [loading, user, profile]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#030213" />
        <Text style={styles.loadingText}>Cargando permisos...</Text>
        {user ? <Text style={styles.debugText}>Usuario: {user.email}</Text> : null}
      </View>
    );
  }

  // Si hay usuario autenticado pero no hay perfil, mostrar error
  if (user && !profile) {
    console.log('🛡️ ProtectedRoute: Usuario sin perfil');
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>
          Tu usuario no tiene un perfil configurado
        </Text>
        <Text style={styles.errorSubtext}>
          Por favor contacta al administrador para configurar tu perfil
        </Text>
        <Text style={styles.debugText}>
          Usuario ID: {user.id}
        </Text>
      </View>
    );
  }

  // Si no hay usuario ni perfil, redirigir a login
  if (!user && !profile) {
    console.log('🛡️ ProtectedRoute: Sin usuario ni perfil, redirigiendo a login');
    return <Navigate to="/login" replace />;
  }

  // Verificar rol requerido
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>
          No tienes permisos para acceder a esta sección
        </Text>
        <Text style={styles.errorSubtext}>
          Se requiere rol: {requiredRole}
        </Text>
      </View>
    );
  }

  // Verificar permiso específico (si se requiere)
  if (requiredPermission) {
    // Esto es asíncrono, así que lo manejaremos en el componente que use este guard
    // Por ahora, solo verificamos el rol
  }

  return <>{children}</>;
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: '#6b7280',
  },
  debugText: {
    marginTop: 8,
    fontSize: 12,
    color: '#9ca3af',
    fontFamily: 'monospace',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#ffffff',
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc2626',
    textAlign: 'center',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
});

